import React, { useEffect, useState, useCallback } from 'react'
import InputMask from 'react-input-mask'
import { useBonusStoreManager } from 'context/HomeStore/Manager/BonusProvider/BonusProvider.manager'
import { LoaderBackdrop } from 'asterX-UI/Loaders'
import {
  Button,
  Grid,
  TextField,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Modal,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@material-ui/core'
import { useSnackbar } from 'notistack'
import debounce from 'lodash/debounce'
import moment from 'moment'
import { BonusService } from 'lib/api/services/bonus.service'
import { LocalStorageService } from 'lib/utils'
import jwtDecode from 'jwt-decode'
import { PROJECT_CODE_BONUSES } from 'constants/roles'
import { thousandSeparator } from 'lib/utils/thousandSeparator'
import { bonusTypes, forWhat } from 'constants/bonus'

const BonusesTab = () => {
  const isMobile = window.innerWidth <= 768
  const { enqueueSnackbar } = useSnackbar()
  const {
    bonusStore: { list, loading, filter },
    bonusStoreFunc: { getListBonuses, onChangeFilter }
  } = useBonusStoreManager()

  const user = jwtDecode(LocalStorageService.getAccessToken())
  const isAdmin = !!user?.roleList?.some(
    el => ['BONUS'].includes(el?.project?.code) && el.code === 'ADMIN'
  )

  const [phoneNumber, setPhoneNumber] = useState(filter?.phoneNumber || '')
  const [previousPhoneNumber, setPreviousPhoneNumber] = useState('')
  const [modalOpen, setModalOpen] = useState(false)
  const [transferModalOpen, setTransferModalOpen] = useState(false)
  const [bulkModalOpen, setBulkModalOpen] = useState(false) // New state for bulk bonuses modal
  const [transferPhoneFrom, setTransferPhoneFrom] = useState('')
  const [transferPhoneTo, setTransferPhoneTo] = useState('')
  const [transferAmount, setTransferAmount] = useState('')
  const [modalPhoneNumber, setModalPhoneNumber] = useState('')
  const [amount, setAmount] = useState('')
  const [actionType, setActionType] = useState('deduct')
  const [projectCode, setProjectCode] = useState(PROJECT_CODE_BONUSES[0].value)
  const [bulkFile, setBulkFile] = useState(null) // New state for bulk bonuses file

  const debouncedFetchBonuses = useCallback(
    debounce(phone => {
      if (phone.length === 18) {
        getListBonuses()
      }
    }, 1000),
    [getListBonuses]
  )

  useEffect(() => {
    if (phoneNumber !== previousPhoneNumber) {
      setPreviousPhoneNumber(phoneNumber)
      debouncedFetchBonuses(phoneNumber)
    }

    return () => {
      debouncedFetchBonuses.cancel()
    }
  }, [phoneNumber, previousPhoneNumber, debouncedFetchBonuses])

  const handleInputChange = event => {
    const { value } = event.target
    setPhoneNumber(value)
    onChangeFilter && onChangeFilter({ target: { name: 'phoneNumber', value } })
  }

  const handleSearch = () => {
    if (phoneNumber.length !== 18) {
      return enqueueSnackbar('Введите корректный номер', { variant: 'warning' })
    } else {
      getListBonuses && getListBonuses()
    }
  }

  const handleModalOpen = () => setModalOpen(true)
  const handleModalClose = () => setModalOpen(false)

  const handleTransferModalOpen = () => setTransferModalOpen(true)
  const handleTransferModalClose = () => setTransferModalOpen(false)

  const handleBulkModalOpen = () => setBulkModalOpen(true) // New function to open bulk bonuses modal
  const handleBulkModalClose = () => setBulkModalOpen(false) // New function to close bulk bonuses modal

  const handleSubmit = async () => {
    const data = {
      phoneNumber: modalPhoneNumber.replace(/[^0-9]/g, ''),
      amount: parseInt(amount, 10),
      projectCode: actionType === 'add' ? projectCode : null
    }

    try {
      if (actionType === 'deduct') {
        await BonusService.deductBonuses(data).then(res =>
          enqueueSnackbar(res.data, { variant: 'success' })
        )
      } else {
        BonusService.addBonuses(data).then(res =>
          enqueueSnackbar(res.data, { variant: 'success' })
        )
      }
      handleModalClose()
      setAmount(0)
    } catch (error) {
      enqueueSnackbar('Произошла ошибка при выполнении операции', {
        variant: 'error'
      })
    }
  }

  const handleTransferSubmit = async () => {
    const data = {
      currentPhoneNumber: transferPhoneFrom.replace(/[^0-9]/g, ''),
      newPhoneNumber: transferPhoneTo.replace(/[^0-9]/g, ''),
      transferAmount: parseInt(transferAmount, 10)
    }

    try {
      await BonusService.transferBonusesAdmin(data).then(res =>
        enqueueSnackbar(res.data, { variant: 'success' })
      )
      handleTransferModalClose()
      setTransferAmount('')
      setTransferPhoneFrom('')
      setTransferPhoneTo('')
    } catch (error) {
      enqueueSnackbar('Произошла ошибка при выполнении операции', {
        variant: 'error'
      })
    }
  }

  const handleBulkFileChange = event => setBulkFile(event.target.files[0]) // New function to handle file input change

  const handleBulkSubmit = async () => {
    const formData = new FormData()
    formData.append('file', bulkFile)

    try {
      await BonusService.addBonusesMultipleAdmin(formData).then(res =>
        enqueueSnackbar(res.data, { variant: 'success' })
      )
      handleBulkModalClose()
      setBulkFile(null)
    } catch (error) {
      enqueueSnackbar('Произошла ошибка при выполнении операции', {
        variant: 'error'
      })
    }
  }

  const renderTransactions = () => {
    if (list && list.transactions && list.transactions.length > 0) {
      return (
        <>
          <Box sx={{ display: 'flex', gap: 35, my: 3 }}>
            <Typography variant="h6">
              Баланс: {thousandSeparator(list.totalAmount)} ₸
            </Typography>
            <Typography variant="h6">
              Заморожено: {thousandSeparator(list.totalFrozen)} ₸
            </Typography>
          </Box>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Баланс</TableCell>
                  <TableCell>Телефон</TableCell>
                  <TableCell>Дата</TableCell>
                  <TableCell>Тип</TableCell>
                  <TableCell>За что</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {list.transactions.map(transaction => (
                  <TableRow key={transaction.id}>
                    <TableCell>{transaction.id}</TableCell>

                    <TableCell>
                      {transaction.transactionType === 'TRANSFER_OUT' ||
                      transaction.transactionType === 'EXPIRE' ||
                      transaction.transactionType === 'SPEND'
                        ? '-'
                        : '+'}
                      {transaction.amount.toString().thousandSeparator()} ₸
                    </TableCell>

                    <TableCell>{transaction.phoneNumber}</TableCell>

                    <TableCell>
                      {moment(transaction.transactionDate).format(
                        'DD MMMM YYYY hh:mm:ss'
                      )}
                    </TableCell>

                    <TableCell>{bonusTypes[transaction.transactionType]}</TableCell>
                    <TableCell>{forWhat[transaction.projectCode]}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )
    } else {
      return <Typography variant="h6">Нет данных для отображения</Typography>
    }
  }

  return (
    <Box>
      <LoaderBackdrop loading={loading} />

      <Box style={{ position: 'relative', height: '70vh' }}>
        <Grid container spacing={3}>
          {isAdmin && (
            <Grid direction="row" item xs={12}>
              <Button
                variant="contained"
                color="default"
                onClick={handleModalOpen}
              >
                Списать/начислить
              </Button>
              <Button
                variant="contained"
                color="default"
                onClick={handleTransferModalOpen}
                style={{ marginTop: '10px' }}
              >
                Перевод
              </Button>
              <Button
                variant="contained"
                color="default"
                onClick={handleBulkModalOpen}
                style={{ marginTop: '10px' }}
              >
                Массовые бонусы
              </Button>
            </Grid>
          )}
          <Grid item xs={isMobile ? 8 : 2}>
            <InputMask
              mask="+7 (999) 999-99-99"
              maskChar={null}
              value={phoneNumber}
              onChange={handleInputChange}
            >
              {inputProps => (
                <TextField
                  {...inputProps}
                  fullWidth
                  label="Бонусы по телефону"
                  name="phoneNumber"
                />
              )}
            </InputMask>
          </Grid>

          <Grid item xs={4}>
            <Button variant="contained" color="primary" onClick={handleSearch}>
              Поиск
            </Button>
          </Grid>

          <Grid item xs={12}>
            {renderTransactions()}
          </Grid>
        </Grid>
      </Box>

      <Modal open={modalOpen} onClose={handleModalClose}>
        <Box
          style={{
            padding: '20px',
            backgroundColor: '#fff',
            margin: 'auto',
            top: '30%',
            position: 'relative',
            width: isMobile ? '88%' : '400px'
          }}
        >
          <Typography variant="h6" gutterBottom>
            Списать/начислить бонусы
          </Typography>
          <FormControl fullWidth margin="normal">
            <InputMask
              mask="+7 (999) 999-99-99"
              maskChar={null}
              value={modalPhoneNumber}
              onChange={e => setModalPhoneNumber(e.target.value)}
            >
              {inputProps => (
                <TextField
                  {...inputProps}
                  fullWidth
                  label="Телефон"
                  name="modalPhoneNumber"
                />
              )}
            </InputMask>
          </FormControl>

          <FormControl fullWidth margin="normal">
            <TextField
              type="number"
              fullWidth
              label="Сумма"
              value={amount}
              onChange={e => setAmount(e.target.value)}
            />
          </FormControl>

          <FormControl fullWidth margin="normal">
            <InputLabel>Операция</InputLabel>
            <Select
              value={actionType}
              onChange={e => setActionType(e.target.value)}
            >
              <MenuItem value="deduct">Списать</MenuItem>
              <MenuItem value="add">Начислить</MenuItem>
            </Select>
          </FormControl>

          {actionType === 'add' && (
            <FormControl fullWidth margin="normal">
              <InputLabel>За что</InputLabel>
              <Select
                value={projectCode}
                onChange={e => setProjectCode(e.target.value)}
              >
                {PROJECT_CODE_BONUSES.map(n => (
                  <MenuItem value={n.value}>{n.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={handleSubmit}
          >
            Отправить
          </Button>
        </Box>
      </Modal>

      <Modal open={transferModalOpen} onClose={handleTransferModalClose}>
        <Box
          style={{
            padding: '20px',
            backgroundColor: '#fff',
            margin: 'auto',
            top: '30%',
            position: 'relative',
            width: isMobile ? '88%' : '400px'
          }}
        >
          <Typography variant="h6" gutterBottom>
            Перевод бонусов
          </Typography>
          <FormControl fullWidth margin="normal">
            <InputMask
              mask="+7 (999) 999-99-99"
              maskChar={null}
              value={transferPhoneFrom}
              onChange={e => setTransferPhoneFrom(e.target.value)}
            >
              {inputProps => (
                <TextField
                  {...inputProps}
                  fullWidth
                  label="Телефон отправителя"
                  name="transferPhoneFrom"
                />
              )}
            </InputMask>
          </FormControl>

          <FormControl fullWidth margin="normal">
            <InputMask
              mask="+7 (999) 999-99-99"
              maskChar={null}
              value={transferPhoneTo}
              onChange={e => setTransferPhoneTo(e.target.value)}
            >
              {inputProps => (
                <TextField
                  {...inputProps}
                  fullWidth
                  label="Телефон получателя"
                  name="transferPhoneTo"
                />
              )}
            </InputMask>
          </FormControl>

          <FormControl fullWidth margin="normal">
            <TextField
              type="number"
              fullWidth
              label="Сумма перевода"
              value={transferAmount}
              onChange={e => setTransferAmount(e.target.value)}
            />
          </FormControl>

          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={handleTransferSubmit}
          >
            Отправить
          </Button>
        </Box>
      </Modal>

      <Modal open={bulkModalOpen} onClose={handleBulkModalClose}>
        <Box
          style={{
            padding: '20px',
            backgroundColor: '#fff',
            margin: 'auto',
            top: '30%',
            position: 'relative',
            width: isMobile ? '88%' : '400px'
          }}
        >
          <Typography variant="h6" gutterBottom>
            Массовые бонусы (загрузи файл)
          </Typography>
          <FormControl fullWidth margin="normal">
            <input
              type="file"
              accept=".xlsx, .xls"
              onChange={handleBulkFileChange}
              multiple={false}
            />
          </FormControl>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={handleBulkSubmit}
          >
            Отправить
          </Button>
        </Box>
      </Modal>
    </Box>
  )
}

export default BonusesTab
